import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, X, Settings, Check } from 'lucide-react';

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    functional: true,
    analytics: true
  });
  const location = useLocation();
  
  // Determine colors based on current route
  const isPropertyServices = location.pathname.startsWith('/ps');
  const primaryColor = isPropertyServices ? '#456CAC' : '#456CAC';
  const primaryColorDark = isPropertyServices ? '#3A5A91' : '#3A5A91';
  const primaryColorLight = isPropertyServices ? '#D1FAE5' : '#E8EEF7';
  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        // Invalid saved data, show banner again
        setShowBanner(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = { essential: true, functional: true, analytics: true };
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(allAccepted);
    setShowBanner(false);
    
    // Enable GA if analytics accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
  };

  const handleAcceptEssential = () => {
    const essentialOnly = { essential: true, functional: false, analytics: false };
    localStorage.setItem('cookie_consent', JSON.stringify(essentialOnly));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(essentialOnly);
    setShowBanner(false);
    
    // Disable GA
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify(preferences));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
    
    // Update GA consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: preferences.analytics ? 'granted' : 'denied'
      });
    }
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 animate-slide-up">
        <div className="max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!showSettings ? (
            // Simple View
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: primaryColorLight }}>
                  <Shield className="w-6 h-6" style={{ color: primaryColor }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                    We value your privacy
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                    We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                    By clicking &quot;Accept All&quot;, you consent to our use of cookies. 
                    <Link to="/cookies" className="hover:underline ml-1" style={{ color: primaryColor }}>
                      Learn more
                    </Link>
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                      style={{ backgroundColor: primaryColor }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = primaryColorDark}
                      onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
                      data-testid="cookie-accept-all"
                    >
                      <Check className="w-4 h-4" />
                      Accept All
                    </button>
                    <button
                      onClick={handleAcceptEssential}
                      className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium rounded-lg transition-colors"
                    >
                      Essential Only
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-5 py-2.5 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-sm font-medium transition-colors flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Customize
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleAcceptEssential}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : (
            // Settings View
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white">Essential Cookies</h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Required for the website to function. Cannot be disabled.</p>
                  </div>
                  <div className="w-12 h-6 rounded-full flex items-center justify-end px-1" style={{ backgroundColor: primaryColor }}>
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white">Functional Cookies</h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Enable personalized features like chat and theme preferences.</p>
                  </div>
                  <button
                    onClick={() => setPreferences({ ...preferences, functional: !preferences.functional })}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.functional ? 'justify-end' : 'bg-slate-300 dark:bg-slate-600 justify-start'
                    }`}
                    style={preferences.functional ? { backgroundColor: primaryColor } : {}}
                  >
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </button>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white">Analytics Cookies</h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Help us understand how visitors interact with our website (Google Analytics).</p>
                  </div>
                  <button
                    onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.analytics ? 'justify-end' : 'bg-slate-300 dark:bg-slate-600 justify-start'
                    }`}
                    style={preferences.analytics ? { backgroundColor: primaryColor } : {}}
                  >
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-5 py-2.5 text-white text-sm font-medium rounded-lg transition-colors"
                  style={{ backgroundColor: primaryColor }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = primaryColorDark}
                  onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
                >
                  Save Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium rounded-lg transition-colors"
                >
                  Accept All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default CookieConsentBanner;
